import { SignUpRequest } from "#/auth/signUp";
import { ThemedFixedButton } from "@/Button";
import { Checkbox, ValidateCheckbox } from "@/Checkbox";
import { Divider } from "@/Divider";
import { Header } from "@/Header";
import { ValidateInput } from "@/Input";
// import { SequentialAnimation } from "@/SequentialAnimation";
import { ValidateUploader } from "@/Uploader";
import { useAppColor } from "^/common/useAppColor";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useContext, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { Controller, useForm } from "react-hook-form";
import { SignupContext } from "@/SignupContext";
import { Dialog } from "@/Dialog";
import { TermsDocContent } from "@/common/TermsDocContent";
import MarketingConsentContent from "@/common/MarketingConsentContent";
import {
  canUseResourceBridge,
  uploadResourceViaBridge,
} from "%/appResourceUpload";
import { isValidBusinessNumber } from "%/businessNumber";
import { checkBusinessNumber } from "../../api/services/auth";

export const Route = createFileRoute("/sign-up/business-owner")({
  component: RouteComponent,
});

export interface SignUpBusinessState extends SignUpRequest {
  storeName: string;
  storeNumber: number;
  businessLicense: string;
  agree1: boolean;
  allAlamYn: boolean;
}

function RouteComponent() {
  const [cookie] = useCookies(["email", "name"]);
  const { setInfo } = useContext(SignupContext);
  const email: string = useMemo(() => cookie.email, [cookie]);
  const name: string = useMemo(() => cookie.name, [cookie]);
  const { history } = useRouter();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitted, isValid },
    watch,
  } = useForm<SignUpBusinessState>({
    defaultValues: {
      storeName: "",
      name: name || "",
      agree1: false,
      allAlamYn: false,
    },
    mode: "onChange",
  });
  const [isPrivacyModalVisible, setIsPrivacyModalVisible] = useState(false);
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
  const [isMarketingModalVisible, setIsMarketingModalVisible] = useState(false);
  const [showValidationErrorDialog, setShowValidationErrorDialog] =
    useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [validationErrorTitle, setValidationErrorTitle] = useState("안내");
  const [showImageFormatHint, setShowImageFormatHint] = useState(false);
  // 에러 다이얼로그를 종류별(제목/이미지안내 여부)로 열기.
  //   기본=일반 안내(이미지 안내 없음). 파일형식 오류일 때만 imageHint=true.
  const openValidationDialog = (
    message: string,
    opts?: { title?: string; imageHint?: boolean },
  ) => {
    setValidationErrorMessage(message);
    setValidationErrorTitle(opts?.title ?? "안내");
    setShowImageFormatHint(opts?.imageHint ?? false);
    setShowValidationErrorDialog(true);
  };

  // 앱(웹뷰)에서는 사업자 사본을 네이티브 브릿지로 업로드 → fileSeq 사용 (구버전 WebView 업로드 실패 우회)
  const useBridge = canUseResourceBridge();
  const [bridgeFileSeq, setBridgeFileSeq] = useState<number | null>(null);
  const [bridgePreview, setBridgePreview] = useState<string>("");
  const [bridgeUploading, setBridgeUploading] = useState(false);

  const allCheck = watch("agree1") && watch("allAlamYn");
  const handleAllCheck = () => {
    if (isSubmitted || !allCheck) {
      setValue("agree1", !allCheck, { shouldValidate: true });
      setValue("allAlamYn", !allCheck, { shouldValidate: true });
    } else {
      setValue("agree1", !allCheck);
      setValue("allAlamYn", !allCheck);
    }
  };
  const bindSubmit = async (data: SignUpBusinessState) => {
    // 앱(브릿지) 경로: 사본은 네이티브로 미리 업로드되어야 함
    if (useBridge && !bridgeFileSeq) {
      openValidationDialog("사업자 등록증을 업로드하세요");
      return;
    }

    // 사업자등록번호 사전 검증(체크섬 + 중복). 다음 단계로 넘어가기 전에 확인.
    try {
      const check = await checkBusinessNumber(String(data.storeNumber));
      if (!check.valid) {
        openValidationDialog(
          check.message ||
            "올바른 사업자등록번호가 아닙니다. 번호를 다시 확인해 주세요.",
          { title: "사업자등록번호 확인" },
        );
        return;
      }
      if (!check.available) {
        const hint = check.maskedEmail
          ? `\n${check.snsType ? check.snsType + " " : ""}${check.maskedEmail} 계정으로 가입되어 있습니다.`
          : "";
        openValidationDialog(`이미 등록된 사업자등록번호입니다.${hint}`, {
          title: "사업자등록번호 확인",
        });
        return;
      }
    } catch {
      openValidationDialog(
        "사업자등록번호 확인 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
      );
      return;
    }

    // 앱이면 File 대신 fileSeq 를 컨텍스트에 저장 → 이후 가입 API 에서 businessFileSeq 로 전송
    setInfo(
      useBridge && bridgeFileSeq
        ? { ...data, businessFileSeq: bridgeFileSeq }
        : data,
    );
    history.push("/sign-up/business-owner-detail");
  };
  useAppColor({
    background: "#ff9934",
  });

  const handlePrivacyModalClose = () => {
    setIsPrivacyModalVisible(false);
  };

  const handleTermsModalClose = () => {
    setIsTermsModalVisible(false);
  };

  const handleMarketingModalClose = () => {
    setIsMarketingModalVisible(false);
  };
  return (
    <div className="flex flex-col flex-auto">
      <Header title="회원가입" className="text-white border-0 bg-yellow" />
      <form className="bg-white flex flex-col flex-auto rounded-t-3xl pt-10 px-5  gap-4 min-h-[calc(100vh-100px)]">
        {/* <SequentialAnimation> */}
        <ValidateInput
          className="w-full"
          placeholder="이메일 주소를 입력하세요"
          label="이메일 주소"
          value={email}
          disabled
          labelClassName="mb-0"
        />
        <ValidateInput
          className="w-full"
          label="상호명"
          placeholder="상호명을 입력하세요"
          errorMessage={errors?.storeName?.message}
          {...register("storeName", {
            required: { value: true, message: "상호명을 입력하세요" },
            minLength: {
              value: 2,
              message: "이름은 최소 2글자 이상이어야 합니다",
            },
          })}
          labelClassName="mb-0"
        />
        <ValidateInput
          className="w-full"
          label="사업자번호"
          regexp="business-number"
          placeholder="사업자번호를 입력하세요"
          errorMessage={errors?.storeNumber?.message}
          {...register("storeNumber", {
            required: { value: true, message: "사업자번호를 입력하세요" },
            pattern: {
              value: /^\d{3}-\d{2}-\d{5}$/,
              message: "올바른 사업자번호 형식을 입력하세요 (예: 123-45-67890)",
            },
            validate: (v) =>
              isValidBusinessNumber(String(v)) ||
              "유효하지 않은 사업자등록번호입니다. 번호를 다시 확인해 주세요.",
          })}
          labelClassName="mb-0"
        />
        {useBridge ? (
          <div className="w-full">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              사업자 등록증
            </label>
            <button
              type="button"
              disabled={bridgeUploading}
              onClick={async () => {
                try {
                  setBridgeUploading(true);
                  const r = await uploadResourceViaBridge(
                    "/api/business/auth/upload-image",
                  );
                  setBridgeFileSeq(r.fileSeq);
                  setBridgePreview(r.fileUrl);
                } catch (e) {
                  openValidationDialog(
                    e instanceof Error
                      ? e.message
                      : "사업자 등록증 업로드에 실패했습니다.",
                    { title: "업로드 오류" },
                  );
                } finally {
                  setBridgeUploading(false);
                }
              }}
              className="flex h-44 w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-gray-300 bg-gray-50 disabled:opacity-60"
            >
              {bridgePreview ? (
                <img
                  src={bridgePreview}
                  alt="사업자 등록증"
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="text-sm text-gray-500">
                  {bridgeUploading ? "업로드 중..." : "사업자 등록증 첨부하기"}
                </span>
              )}
            </button>
          </div>
        ) : (
          <Controller
            name="businessLicense"
            control={control}
            rules={{ required: "사업자 등록증을 업로드하세요" }}
            render={(dd) => (
              <ValidateUploader
                onUploaded={dd.field.onChange}
                onValidationError={(error) => {
                  openValidationDialog(error, {
                    title: "파일 형식 오류",
                    imageHint: true,
                  });
                }}
                errorMessage={errors?.businessLicense?.message}
                className="w-full"
                label="사업자 등록증"
                maxSizeMB={20}
              />
            )}
          />
        )}
        <ValidateInput
          className="w-full"
          label="이름"
          placeholder="이름을 입력하세요"
          errorMessage={errors?.name?.message}
          {...register("name", {
            required: { value: true, message: "이름을 입력하세요" },
            minLength: {
              value: 2,
              message: "이름은 최소 2글자 이상이어야 합니다",
            },
            pattern: {
              value: /^[가-힣]+$/,
              message: "한글만 입력 가능합니다",
            },
          })}
          labelClassName="mb-0"
        />
        <ValidateInput
          className="w-full"
          label="휴대폰번호"
          type="tel"
          errorMessage={errors?.phone?.message}
          {...register("phone", {
            required: { value: true, message: "휴대폰 번호를 입력하세요" },
            pattern: {
              value: /^\d{3}-\d{3,4}-\d{4}$/,
              message: "유효한 휴대폰 번호를 입력하세요",
            },
          })}
          labelClassName="mb-0"
        />
        {/* </SequentialAnimation> */}
        <Divider className="mt-auto" />

        {/* <SequentialAnimation initialDelay={0.2}> */}
        <Checkbox
          title="전체 동의"
          checked={allCheck}
          onChange={handleAllCheck}
        />
        <Divider line />
        <Controller
          name="agree1"
          control={control}
          rules={{ required: "필수 동의입니다." }}
          render={({ field }) => (
            <ValidateCheckbox
              defaultChecked={field.value}
              onChange={() => field.onChange(!field.value)}
              errorMessage={errors?.agree1?.message}
              title="[필수] 멍투데이 서비스 이용 약관 및 개인정보처리방침에 동의합니다."
            />
          )}
        />
        <div className="flex gap-2 pl-8">
          <p
            onClick={() => setIsTermsModalVisible(true)}
            className="text-sm mt-0.5 text-orange-400 cursor-pointer"
          >
            [이용약관]
          </p>
          <p
            onClick={() => setIsPrivacyModalVisible(true)}
            className="text-sm mt-0.5 text-orange-400 cursor-pointer"
          >
            [개인정보처리방침]
          </p>
        </div>
        <Controller
          name="allAlamYn"
          control={control}
          render={({ field }) => (
            <ValidateCheckbox
              defaultChecked={field.value}
              onChange={() => field.onChange(!field.value)}
              errorMessage={errors?.allAlamYn?.message}
              title="[선택] 광고성 정보 수신 전체 동의"
              desc="마케팅정보 수집에 동의하시면 유치원 할인, 프로모션 정보 등을 받아보실 수 있습니다."
            />
          )}
        />
        <p
          onClick={() => setIsMarketingModalVisible(true)}
          className="text-sm mt-0.5 text-orange-400 pl-8 cursor-pointer mb-5 pb-2"
        >
          [마케팅정보 수신 동의]
        </p>
        {/* <Checkbox
                    title="[선택] 광고성 정보 수신 전체 동의"
                    desc="마케팅정보 수집에 동의하시면 유치원 할인, 프로모션 정보 등을 받아보실 수 있습니다."
                  /> */}
        {/* </SequentialAnimation> */}
      </form>

      <ThemedFixedButton
        disabled={!isValid}
        onClick={handleSubmit(bindSubmit)}
        color="primary"
        size="md"
      >
        다음 단계로
      </ThemedFixedButton>

      {/* 개인정보처리방침 모달 */}
      <Dialog visible={isPrivacyModalVisible} onClose={handlePrivacyModalClose}>
        <div className="flex flex-col max-h-[60vh] w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="ml-4 text-lg font-bold">개인정보처리방침</h2>
            <button
              onClick={handlePrivacyModalClose}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="overflow-y-auto w-full h-full">
            <TermsDocContent
              targetRole="BUSINESS"
              termsType="TERMS_TYPE_PRIVACY"
            />
          </div>
        </div>
      </Dialog>
      {/* 이용약관 모달 */}
      <Dialog visible={isTermsModalVisible} onClose={handleTermsModalClose}>
        <div className="flex flex-col max-h-[60vh] w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="ml-4 text-lg font-bold">이용약관</h2>
            <button
              onClick={handleTermsModalClose}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="overflow-y-auto w-full h-full">
            <TermsDocContent
              targetRole="BUSINESS"
              termsType="TERMS_TYPE_SERVICE"
            />
          </div>
        </div>
      </Dialog>
      {/* 마케팅정보 수신 동의 모달 */}
      <Dialog
        visible={isMarketingModalVisible}
        onClose={handleMarketingModalClose}
      >
        <div className="flex flex-col max-h-[60vh] w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="ml-4 text-lg font-bold">마케팅 정보 수신동의</h2>
            <button
              onClick={handleMarketingModalClose}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="overflow-y-auto w-full h-full">
            <MarketingConsentContent />
          </div>
        </div>
      </Dialog>

      {/* 파일 검증 에러 Dialog */}
      <Dialog
        visible={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">{validationErrorTitle}</h3>
          <p className="mb-4 text-gray-600 whitespace-pre-line">
            {validationErrorMessage}
          </p>
          {showImageFormatHint && (
            <p className="mb-4 text-sm text-gray-500">
              이미지 파일만 업로드 가능합니다. (JPG, PNG, GIF, WEBP, BMP, SVG)
            </p>
          )}
          <button
            onClick={() => setShowValidationErrorDialog(false)}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>
    </div>
  );
}
